/**
 * Network error types and utilities
 */

import type { ErrorCode, HttpMethod, NetworkError as INetworkError } from './types.js';

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);
const RETRYABLE_ERROR_CODES: Set<ErrorCode> = new Set(['TIMEOUT', 'NETWORK']);

export class NetworkError extends Error implements INetworkError {
  readonly code: ErrorCode;
  readonly status?: number;
  readonly statusText?: string;
  readonly response?: Response;
  readonly responseData?: unknown;
  readonly retryable: boolean;
  readonly cause?: Error;
  readonly url?: string;
  readonly method?: HttpMethod;

  constructor(
    message: string,
    options: {
      code: ErrorCode;
      status?: number;
      statusText?: string;
      response?: Response;
      responseData?: unknown;
      retryable?: boolean;
      cause?: Error;
      url?: string;
      method?: HttpMethod;
    }
  ) {
    super(message);
    this.name = 'NetworkError';
    this.code = options.code;
    this.status = options.status;
    this.statusText = options.statusText;
    this.response = options.response;
    this.responseData = options.responseData;
    this.cause = options.cause;
    this.url = options.url;
    this.method = options.method;

    // Determine if retryable
    if (options.retryable !== undefined) {
      this.retryable = options.retryable;
    } else if (options.status !== undefined) {
      this.retryable = RETRYABLE_STATUS_CODES.has(options.status);
    } else {
      this.retryable = RETRYABLE_ERROR_CODES.has(options.code);
    }

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }
  }

  /** Create a timeout error */
  static timeout(url: string, method: HttpMethod, timeoutMs: number): NetworkError {
    return new NetworkError(`Request timeout after ${timeoutMs}ms`, {
      code: 'TIMEOUT',
      url,
      method,
      retryable: true,
    });
  }

  /** Create a network error (connection failed, DNS error, etc.) */
  static network(url: string, method: HttpMethod, cause?: Error): NetworkError {
    return new NetworkError(cause?.message ?? 'Network request failed', {
      code: 'NETWORK',
      url,
      method,
      cause,
      retryable: true,
    });
  }

  /** Create an abort error (request was cancelled) */
  static abort(url: string, method: HttpMethod): NetworkError {
    return new NetworkError('Request was aborted', {
      code: 'ABORT',
      url,
      method,
      retryable: false,
    });
  }

  /** Create an HTTP error (non-2xx response) */
  static http(
    url: string,
    method: HttpMethod,
    response: Response,
    responseData?: unknown
  ): NetworkError {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    if (responseData && typeof responseData === 'object') {
      const data = responseData as Record<string, unknown>;
      if ('message' in data && typeof data.message === 'string') {
        message = data.message;
      } else if ('error' in data) {
        const err = data.error;
        if (typeof err === 'string') {
          message = err;
        } else if (err && typeof err === 'object') {
          const errObj = err as Record<string, unknown>;
          if (typeof errObj.message === 'string') message = errObj.message;
          else message = JSON.stringify(err);
        }
      } else if ('message' in data) {
        message = JSON.stringify(data.message);
      }
    }

    return new NetworkError(message, {
      code: 'HTTP',
      status: response.status,
      statusText: response.statusText,
      response,
      responseData,
      url,
      method,
    });
  }

  /** Create a parse error (failed to parse response) */
  static parse(url: string, method: HttpMethod, cause?: Error): NetworkError {
    return new NetworkError('Failed to parse response', {
      code: 'PARSE',
      url,
      method,
      cause,
      retryable: false,
    });
  }

  /** Wrap an unknown error */
  static from(error: unknown, url?: string, method?: HttpMethod): NetworkError {
    if (error instanceof NetworkError) {
      return error;
    }

    if (error instanceof Error) {
      // Check for abort
      if (error.name === 'AbortError') {
        return NetworkError.abort(url ?? 'unknown', method ?? 'GET');
      }

      // Check for timeout (some environments use this)
      if (error.name === 'TimeoutError') {
        return NetworkError.timeout(url ?? 'unknown', method ?? 'GET', 0);
      }

      // Check for network errors (TypeError usually indicates network failure in fetch)
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return NetworkError.network(url ?? 'unknown', method ?? 'GET', error);
      }

      return new NetworkError(error.message, {
        code: 'UNKNOWN',
        cause: error,
        url,
        method,
        retryable: false,
      });
    }

    return new NetworkError(String(error), {
      code: 'UNKNOWN',
      url,
      method,
      retryable: false,
    });
  }

  /** Check if an error is a NetworkError */
  static isNetworkError(error: unknown): error is NetworkError {
    return error instanceof NetworkError;
  }

  /** Check if an error is retryable */
  static isRetryable(error: unknown): boolean {
    if (error instanceof NetworkError) {
      return error.retryable;
    }
    return false;
  }

  /** Check if an error is a specific HTTP status */
  static isStatus(error: unknown, status: number): boolean {
    return error instanceof NetworkError && error.status === status;
  }

  /** Check if error is a client error (4xx) */
  static isClientError(error: unknown): boolean {
    return error instanceof NetworkError &&
      error.status !== undefined &&
      error.status >= 400 &&
      error.status < 500;
  }

  /** Check if error is a server error (5xx) */
  static isServerError(error: unknown): boolean {
    return error instanceof NetworkError &&
      error.status !== undefined &&
      error.status >= 500;
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      statusText: this.statusText,
      retryable: this.retryable,
      url: this.url,
      method: this.method,
    };
  }
}
